/**
 * Debounced auto-save data layer for {@link components/workspace/WorkspaceSettings}
 * (audit item 30 / A1). Owns dirty-field tracking, the debounce timer, the
 * PATCH to `brands`, the permission gate, and the zero-row-as-failure +
 * dirty-field re-arm guards from item 23 (A12).
 *
 * Behaviour contract (do not regress item 23):
 *  - A member without `brands:edit` never issues the PATCH (it would be
 *    RLS-filtered to zero rows with no error and silently "succeed").
 *  - The PATCH ends in `.select('id')`; a zero-row result is treated as a
 *    failure (no error returned by PostgREST in that case).
 *  - On any failure the attempted fields are re-added to the dirty set so the
 *    next save retries them instead of dropping them.
 *
 * @module components/hooks/useWorkspaceAutoSave
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { BrandOffering } from '../../lib/org-types';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { isSafeQuestionLinkUrl } from '../../lib/question-blocks';

/** Auto-save debounce window (ms). Tuned for "type a word, then it lands". */
export const AUTO_SAVE_DEBOUNCE_MS = 300;

/** Editable workspace fields tracked for auto-save. */
export interface WorkspaceAutoSaveForm {
  name: string;
  color: string;
  website_url: string;
  privacy_policy_url: string;
  summary: string;
  target_audience: string;
  brand_voice: string;
  brand_tone: string;
  value_proposition: string;
  keywords: string[];
  offerings: BrandOffering[];
}

/** True for an absolute HTTP(S) URL without embedded credentials. */
export function isSafeHttpUrl(value: string): boolean {
  return isSafeQuestionLinkUrl(value);
}

/** Slug helper: lower-cases, replaces non-alphanumerics with hyphens, trims. */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/** Minimal workspace shape this hook writes to. */
interface AutoSaveWorkspace {
  id: string;
  organization_id: string;
}

/** Inputs for {@link useWorkspaceAutoSave}. */
export interface UseWorkspaceAutoSaveArgs {
  /** The active workspace row, or null when none is selected. */
  workspace: AutoSaveWorkspace | null;
  /** Current form values (read fresh on every flush via a live ref). */
  form: WorkspaceAutoSaveForm;
  /** Whether the viewer holds `brands:edit`. Gates the PATCH (A12). */
  canEdit: boolean;
  /** Supabase client. */
  supabase: SupabaseClient;
  /** Acting user id, for telemetry context. */
  userId: string | undefined;
  /** Re-fetch workspaces after a successful save. */
  refreshWorkspaces: () => Promise<void> | void;
}

/** Return contract of {@link useWorkspaceAutoSave}. */
export interface UseWorkspaceAutoSaveResult {
  /** Mark a field dirty and (re)arm the debounce. No-op while rebasing. */
  scheduleAutoSave: (field: keyof WorkspaceAutoSaveForm) => void;
  /** Suspend dirty-tracking + clear pending state during a form rebase. */
  beginRebase: () => void;
  /** Resume dirty-tracking after a rebase yields one tick. */
  endRebase: () => void;
  /** Transient user-facing save error, or null. */
  saveError: string | null;
  /** Clear the save error (e.g. when the user resumes editing). */
  clearSaveError: () => void;
}

/**
 * Build the PATCH payload for the given dirty fields from the current form.
 * Mirrors the original WorkspaceSettings field mapping (trim-to-null, slug
 * derivation from name, etc.). Pure so it can be unit-tested directly.
 */
export function buildWorkspacePatch(
  dirtyFields: Array<keyof WorkspaceAutoSaveForm>,
  form: WorkspaceAutoSaveForm,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const field of dirtyFields) {
    switch (field) {
      case 'name': {
        const trimmedName = form.name.trim();
        patch.name = trimmedName;
        // Keep `slug` in sync with `name` for `brands_slug_per_org` uniqueness.
        const derivedSlug = slugify(trimmedName);
        patch.slug = derivedSlug === '' ? null : derivedSlug;
        break;
      }
      case 'color':
        patch.color = form.color;
        break;
      case 'website_url':
        patch.website_url =
          form.website_url.trim() === '' ? null : form.website_url.trim();
        break;
      case 'privacy_policy_url':
        patch.privacy_policy_url =
          form.privacy_policy_url.trim() === ''
            ? null
            : form.privacy_policy_url.trim();
        patch.privacy_policy_url_source = 'manual';
        break;
      case 'summary':
        patch.summary = form.summary.trim() === '' ? null : form.summary;
        break;
      case 'target_audience':
        patch.target_audience =
          form.target_audience.trim() === '' ? null : form.target_audience;
        break;
      case 'brand_voice':
        patch.brand_voice =
          form.brand_voice.trim() === '' ? null : form.brand_voice;
        break;
      case 'brand_tone':
        patch.brand_tone =
          form.brand_tone.trim() === '' ? null : form.brand_tone;
        break;
      case 'value_proposition':
        patch.value_proposition =
          form.value_proposition.trim() === ''
            ? null
            : form.value_proposition;
        break;
      case 'keywords':
        patch.keywords = form.keywords.length > 0 ? form.keywords : null;
        break;
      case 'offerings':
        patch.offerings = form.offerings;
        break;
    }
  }
  return patch;
}

/**
 * Debounced auto-save for the workspace settings form. See the module docs for
 * the permission / zero-row / re-arm contract (item 23).
 */
export function useWorkspaceAutoSave({
  workspace,
  form,
  canEdit,
  supabase,
  userId,
  refreshWorkspaces,
}: UseWorkspaceAutoSaveArgs): UseWorkspaceAutoSaveResult {
  const [saveError, setSaveError] = useState<string | null>(null);
  const dirtyRef = useRef<Set<keyof WorkspaceAutoSaveForm>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rebasingRef = useRef(false);

  // Live mirrors so the debounced timer always reads the freshest values
  // without re-binding (which would PATCH stale data).
  const formRef = useRef(form);
  formRef.current = form;
  const workspaceRef = useRef(workspace);
  workspaceRef.current = workspace;
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const refreshRef = useRef(refreshWorkspaces);
  refreshRef.current = refreshWorkspaces;

  const flushAutoSave = useCallback(async () => {
    const activeWorkspace = workspaceRef.current;
    if (!activeWorkspace) return;
    const dirtyFields = Array.from(dirtyRef.current);
    if (dirtyFields.length === 0) return;

    // Gate before clearing: a member without `brands:edit` would have its
    // PATCH RLS-filtered to zero rows with no error, so the old code silently
    // "succeeded". Fail fast and keep the fields dirty for a later retry.
    if (!canEditRef.current) {
      setSaveError("You don't have permission to edit this workspace.");
      return;
    }

    if (
      dirtyFields.includes('privacy_policy_url') &&
      formRef.current.privacy_policy_url.trim() !== '' &&
      !isSafeHttpUrl(formRef.current.privacy_policy_url.trim())
    ) {
      setSaveError('Enter a valid privacy policy URL.');
      return;
    }

    // Clear the dirty set up front so concurrent edits accumulate a fresh
    // batch, but stash the snapshot so a failed save can re-arm exactly the
    // fields it tried to write.
    dirtyRef.current.clear();
    const reArmDirty = () => {
      for (const field of dirtyFields) dirtyRef.current.add(field);
    };

    const patch = buildWorkspacePatch(dirtyFields, formRef.current);

    setSaveError(null);
    try {
      // `.select('id')` turns the PATCH into a row-returning query so a
      // zero-row result (RLS-filtered, no error) is detectable.
      const { data, error } = await supabase
        .from('brands')
        .update(patch)
        .eq('id', activeWorkspace.id)
        .select('id');
      if (error) {
        captureException(new Error(error.message), 'WorkspaceSettings.autoSave', {
          workspaceId: activeWorkspace.id,
          organizationId: activeWorkspace.organization_id,
          userId: userIdRef.current,
        });
        reArmDirty();
        setSaveError("Couldn't save.");
        return;
      }
      if (!data || data.length === 0) {
        captureException(
          new Error('Workspace update matched zero rows'),
          'WorkspaceSettings.autoSave',
          {
            workspaceId: activeWorkspace.id,
            organizationId: activeWorkspace.organization_id,
            userId: userIdRef.current,
          },
        );
        reArmDirty();
        setSaveError("Couldn't save.");
        return;
      }
      void refreshRef.current();
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'WorkspaceSettings.autoSave',
        {
          workspaceId: activeWorkspace.id,
          organizationId: activeWorkspace.organization_id,
          userId: userIdRef.current,
        },
      );
      reArmDirty();
      setSaveError("Couldn't save.");
    }
  }, [supabase]);

  /** Latest flushAutoSave mirrored so the timer reads the freshest closure. */
  const flushLiveRef = useRef(flushAutoSave);
  flushLiveRef.current = flushAutoSave;

  const scheduleAutoSave = useCallback(
    (field: keyof WorkspaceAutoSaveForm) => {
      if (rebasingRef.current) return;
      dirtyRef.current.add(field);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        void flushLiveRef.current();
      }, AUTO_SAVE_DEBOUNCE_MS);
    },
    [],
  );

  const beginRebase = useCallback(() => {
    rebasingRef.current = true;
    dirtyRef.current.clear();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const endRebase = useCallback(() => {
    rebasingRef.current = false;
  }, []);

  const clearSaveError = useCallback(() => setSaveError(null), []);

  // Drain any pending debounce on unmount so an in-flight edit isn't lost.
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        void flushLiveRef.current();
      }
    };
  }, []);

  return { scheduleAutoSave, beginRebase, endRebase, saveError, clearSaveError };
}
